import React, { type FC, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete'; 
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import './FileUpload.scss';

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  onFileError?: (error: boolean) => void;
  // We can set the localized error message from the server
  uploadError?: string;
  // Or We can set the error code returned from the server
  uploadErrorCode?: string;
}

type ServerErrorMessageProps = { fileName: string, message?: string, code?: string };

const MAX_SIZE = 15 * 1024 * 1024; // 15MB in bytes

const ServerErrorMessage: FC<ServerErrorMessageProps> = ({ message, fileName, code }) => {
  const { t } = useTranslation("fileupload", { useSuspense: false });

  const getErrorMessageFromCode = (code: string, fileName?: string) =>
    (["1002", "1003", "1005", "1008", "1013"].includes(code) && fileName)
      ? `${t(code)} ${t("for_file")} "${fileName}"`
      : `${t('doc_upload_failed')}: ${t(code)}`;

  return (
    <div className="server-error">
      <div className="error-icon"><ErrorIcon /></div>
      <div className="error-content">
        <div className="error-message">{t('doc_failed_to_save')}</div>
        <div className="error-details">{message || getErrorMessageFromCode(code!, fileName)}</div>
      </div>
    </div>
  );
}

export const FileUpload: FC<FileUploadProps> = ({ onFileSelect, onFileError, uploadError, uploadErrorCode }) => {
  const { t } = useTranslation("fileupload", { useSuspense: false });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [fileError, setFileError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const supportedFormats = ['.pdf', '.docx', '.png', '.jpg', '.jpeg', '.tiff'];
  const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg', 'image/tiff'];
  
  useEffect(() => {
    onFileError?.(!!fileError);
  }, [fileError]);

  const isFileSupported = (file: File) => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    return supportedFormats.includes(extension);
  };

  const handleFileSelect = (file: File | null) => {
    setError('');
    setFileError('');
    if (file) {
      if (!isFileSupported(file)) {
        setFileError(t('file_format_not_supported'));
      } else if (file.size > MAX_SIZE) {
        setFileError(t('file_size_exceeds_15MB_limit'));
      }
      assignSelectedFile(file);
    } else {
      assignSelectedFile(null);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    handleFileSelect(file);
  };

  const handleDelete = () => {
    assignSelectedFile(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const assignSelectedFile = (file: File | null) => {
    setSelectedFile(file);
    onFileSelect(file);
  };

  const handleBrowseClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    fileInputRef.current?.click();
  };

  // ignoring drag and drop handlers since they rely on browser APIs that are difficult to test in Jest's jsdom environment
  /* istanbul ignore next */
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    const { type: fileType, kind } = event.dataTransfer.items?.[0] || { type: null, kind: null };
    if ((!!fileType || kind === 'file') && !validTypes.includes(fileType)) {
      setError(t('file_format_not_supported'));
    } else {
      setError("");
    }
    setIsDragging(true);
  };

  // ignoring drag and drop handlers since they rely on browser APIs that are difficult to test in Jest's jsdom environment
  /* istanbul ignore next */
  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    const relatedTarget = event.relatedTarget as Node;
    const container = event.currentTarget as HTMLElement;
    
    if (relatedTarget instanceof Node && container.contains(relatedTarget)) {
      return;
    }
    
    setError("");
    setIsDragging(false);
  };

  // ignoring drag and drop handlers since they rely on browser APIs that are difficult to test in Jest's jsdom environment
  /* istanbul ignore next */
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0] || null;
    setError("");
    handleFileSelect(file);
  };

  return (
    <div className="file-upload-container">
      { (
      <div
        className={`upload-area ${isDragging ? 'dragging' : ''} ${error ? 'error' : ''}`} 
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleInputChange}
          accept=".pdf,.docx,.png,.jpg,.jpeg,.tiff"
          className="file-input"
        />
        <div className="upload-content">
          <UploadFileIcon className="file-icon" />
          <div className="browse-text">
            <button onClick={handleBrowseClick} className="browse-link">
            {t('browse_file')}
            </button>
            <span className="or-text">{`${t('or')} ${t('drag_and_drop')}`}</span>
          </div>
          {error && (
            <div className="error-message">
              <ErrorIcon className="error-icon" />
              <span className="error-text">{error}</span>
            </div>
          )}
          {!error && (
            <div className="file-types">
              PDF, DOCX, PNG, JPG {t('or')} TIFF ({t('max')}. 15 {t('mb')})
            </div>
          )}
        </div>
      </div>
      )}

      {selectedFile && !(uploadError || uploadErrorCode) && (
        <div className="file-info">
          <div className={`file-details${!!fileError ? " error" : ""}`}>
            <UploadFileIcon className="file-icon" />
            <div className="file-name-section">
              <a href={URL.createObjectURL(selectedFile)} target="_blank" rel="noopener noreferrer" className='file-name'>
                {selectedFile.name}
              </a>
              <div className="file-meta">
                {!!fileError ? <span>{fileError}</span> :(
                  <>
                    <span className="file-size">
                      {selectedFile.size > 1024 * 1024
                      ? `${(selectedFile.size / (1024 * 1024)).toFixed(1)} ${t('mb')}`
                      : `${(selectedFile.size / 1024).toFixed(1)} ${t('kb')}`}
                    </span>
                    <span>&bull; {t('selected')}</span>
                  </>
                )}
              </div>
            </div>
          </div>
            <button
              onClick={handleDelete}
              className="delete-button"
              aria-label={t('delete_file')}
            >
              <DeleteIcon />
            </button>
        </div>
      )}
      {selectedFile && (!!uploadError || !!uploadErrorCode) && (
        <ServerErrorMessage fileName={selectedFile.name} message={uploadError!} code={uploadErrorCode!} />
      )}
    </div>
  );
};

export default FileUpload;